import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';

interface CountdownPart {
  value: string;
  label: string;
}

/**
 * Countdown for the platform sale (dates from the platformSaleData API):
 * before start_at -> "Sale starts in", between start_at and end_at -> "Sale ends in", after end_at -> hidden.
 * Also shows the enrollment (start_at - end_at) and sale (start_at - end_at) dates.
 * The 1s timer runs outside Angular and only this component is re-rendered (OnPush + detectChanges).
 * Usage: <ipm-sale-countdown [startAt]="..." [endAt]="..." [enrollmentStartAt]="..." appearance="inline"></ipm-sale-countdown>
 */
@Component({
  selector: 'ipm-sale-countdown',
  templateUrl: './sale-countdown.component.html',
  styleUrls: ['./sale-countdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaleCountdownComponent implements OnChanges, OnDestroy {
  /** start_at (enrollment runs until end_at) */
  @Input() enrollmentStartAt: string | null = null;
  @Input() startAt: string | null = null;
  @Input() endAt: string | null = null;
  /** 'card': dark card (enrol page),
   *  'inline': one line ("⏱ Sale starts in 09d 15h 27m 32s"), for the Diwali banner, header and compact cards */
  @Input() appearance: 'card' | 'inline' = 'card';

  label: string = '';
  parts: Array<CountdownPart> = [];
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private changeDetector: ChangeDetectorRef, private ngZone: NgZone) { }

  ngOnChanges(): void {
    this.tick();
    if (!this.timer && this.label) {
      this.ngZone.runOutsideAngular(() => {
        this.timer = setInterval(() => this.tick(), 1000);
      });
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private tick(): void {
    const now = Date.now();
    const start = this.startAt ? new Date(this.startAt).getTime() : NaN;
    const end = this.endAt ? new Date(this.endAt).getTime() : NaN;
    let target = NaN;
    if (now < start) {
      this.label = 'Sale starts in';
      target = start;
    } else if (now <= end) {
      this.label = 'Sale ends in';
      target = end;
    } else {
      this.label = '';
      this.stopTimer();
    }
    this.parts = isNaN(target) ? [] : this.toParts(target - now);
    this.changeDetector.detectChanges();
  }

  private toParts(milliseconds: number): Array<CountdownPart> {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const pad = (value: number) => String(value).padStart(2, '0');
    return [
      { value: pad(Math.floor(totalSeconds / 86400)), label: 'Days' },
      { value: pad(Math.floor((totalSeconds % 86400) / 3600)), label: 'Hours' },
      { value: pad(Math.floor((totalSeconds % 3600) / 60)), label: 'Mins' },
      { value: pad(totalSeconds % 60), label: 'Secs' },
    ];
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
